import React, {Suspense, useCallback, useEffect, useRef, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {Link, useParams} from "react-router-dom";
import AutoComplete from "@mui/material/Autocomplete";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
import Anatomy from "./Anatomy";
import {Canvas} from "@react-three/fiber";
import {Html, OrbitControls} from "@react-three/drei";
import SpeechRecognition, {useSpeechRecognition} from "react-speech-recognition";
import {baseUrl, ipAddress, userLocation, userName} from "../Univ.js";


/* =====================================================================
   CONFIG
   ===================================================================== */

/*
 * !!! CHECK THIS ONE !!!
 * API that returns the patient's PREVIOUS consultation records (the same
 * data your "Past Visit" / EMR page shows). Each record is expected to look
 * like a consultform record (drug_id, remarks, freq, ddays, dose, dnotes,
 * diagnosis, complaints ...). Point it to the URL your EMR page calls.
 */
const PAST_VISITS_URL = (pat) => `${baseUrl}emr-op/${pat?.id}`;

// The emr-op/op-bill row's own `id` is NOT necessarily the appointment FK.
// Only use an explicit appointment reference for view-consultform.
const numericId = (value) => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "object") return numericId(value.id ?? value.pk ?? value.value);
    const text = String(value).trim();
    return /^\d+$/.test(text) ? text : null;
};
const getConsultAppointmentId = (row) => {
    if (!row || typeof row !== "object") return null;
    // Intentionally do NOT use row.id here: for emr-op rows it can be the
    // OP-bill/EMR primary key rather than the appointment primary key.
    const candidates = [
        row.appointment_id, row.appointmentId,
        row.appointment_data?.id, row.appt_data?.id,
        row.emr?.appointment_id, row.emr?.appointment?.id,
        row.appt?.id, row.appointment?.id,
    ];
    for (const candidate of candidates) {
        const id = numericId(candidate);
        if (id) return id;
    }
    return null;
};

const getPublicAppointmentRef = (row) => {
    if (!row || typeof row !== "object") return null;

    const candidates = [
        row.appt_id,
        row.apptId,
        row.appointment?.appt_id,
        row.appt?.appt_id,
        row.appointment_data?.appt_id,
        row.appt_data?.appt_id,
        row.emr?.appt_id,
        row.emr?.appointment?.appt_id,
    ];

    return (
        candidates.find(
            (value) =>
                typeof value === "string" &&
                value.trim() &&
                !/^\d+$/.test(value.trim())
        )?.trim() || null
    );
};

// Returns the numeric Appointment primary key only.
const getAppointmentPk = (row) => {
    if (!row || typeof row !== "object") return null;

    const candidates = [
        row.appointment_pk,
        row.appointment_id,
        row.appointment?.id,
        row.appt?.id,
        row.appointment_data?.id,
        row.appt_data?.id,
        row.emr?.appointment?.id,
        row.id,
    ];

    for (const value of candidates) {
        if (
            value !== null &&
            value !== undefined &&
            /^\d+$/.test(String(value).trim())
        ) {
            return Number(value);
        }
    }

    return null;
};
const responseRows = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.data)) return data.data;
    return data && typeof data === "object" ? [data] : [];
};

/*
 * Saved lab / radiology requests are NOT pre-loaded into the editable rows
 * (submitting would create a duplicate request). Set true to restore that.
 */
const PREFILL_SAVED_ORDERS = false;

const FAV_KEY = `drhis_fav_meds_${userName || "default"}`;
const REFILL_TEXT_FIELDS = ["allergy", "illness", "complaints", "diagnosis", "tsugg", "tadvise", "diet", "physio", "examination", "pat_type", "duration_tt"];

/* =====================================================================
   STATIC DATA
   ===================================================================== */

const TABS = [
    {id: "case", label: "Case Sheet", icon: "fa-notes-medical"},
    {id: "lab", label: "Lab", icon: "fa-flask-vial"},
    {id: "inv", label: "Radiology", icon: "fa-x-ray"},
    {id: "min", label: "Min. Treat", icon: "fa-syringe"},
    {id: "med", label: "Medical Summary", icon: "fa-folder-open"},
    {id: "map", label: "Case Sheet Map", icon: "fa-cube"},
];

const VITALS = [
    ["weight", "Weight (Kg)", "number", "fa-weight-scale"],
    ["height", "Height (cm)", "number", "fa-ruler-vertical"],
    ["bmi", "BMI", "number", "fa-calculator", true],
    ["tempr", "Tempr.", "number", "fa-temperature-half"],
    ["pulse", "Pulse", "number", "fa-heart-pulse"],
    ["bp", "BP (Sys/Dias)", "text", "fa-gauge-high"],
    ["spo2", "SPO2", "number", "fa-lungs"],
    ["sugar", "Sugar", "number", "fa-droplet"],
];

const GENERAL_NOTES = [
    ["illness", "P/ Medical H/O"], ["complaints", "P/ Surgical H/O"], ["allergy", "Allergy H/O"],
    ["diet", "Investigations/ Imaging"], ["diagnosis", "Diagnosis"], ["tsugg", "Treatment/ Plan"],
    ["tadvise", "Advise"], ["physio", "Opinion/Referal"],
];

const HARD_FIELDS = ["Calculus", "Stains", "Dental Caries", "Missing", "Mobility"];
const SOFT_FIELDS = ["Gingiva", "Periodontium", "Oral Mucosa"];

const CHIEF_LIST = [
    ["pain", "Pain/Swelling"], ["badBreath", "Bad Breath"], ["thyroid", "Thyroid"], ["malalign", "Malalign"],
    ["decay", "Decay"], ["sensitivity", "Sensitivity"], ["replacement", "Replacement"],
];
const MEDHIST_LIST = [
    ["jaundice", "Jaundice"], ["highBP", "High Blood Pressure"], ["heartDisease", "Heart Disease"],
    ["bleedingDisorder", "Bleeding Disorders"], ["heamophilia", "Heamophilia"], ["anaemia", "Anaemia"],
    ["fits", "Fits"], ["asthma", "Asthma / RS Problems"], ["thyroid", "Thyroid"], ["diabetes", "Diabetes"],
    ["renal", "Renal Diseases"],
];

const SYMPTOMS = ["Headache", "Fever", "Running Nose", "Sore Throat", "Sneezing", "Cough", "Sputum", "Wheeze", "Dyspnea", "PND", "Pedal Oedema", "Anasarca", "Puffy Face", "Swelling", "Neck Swelling", "Groin", "Pain", "Itching", "Urticaria", "Weakness", "Vertigo", "Unsteadiness", "Chest Pain", "Burning Micturition", "Frequency Of Urine", "Urgency", "Diarrhea", "Constipation", "Bleeding PR", "Epigastric Pain", "Loin Pain", "Loin Pain Radiating to Rt Groin", "Loin Pain Radiating to Lt Groin", "LBA", "LBA Radiating to Rt Thigh", "LBA Radiating to Lt Thigh", "Neck Pain", "Neck Pain Radiating to Rt Upper Limb", "Neck Pain Radiating to Left Upper Limb"];

const D = (pairs) => pairs.map(([name, label]) => ({name, label}));
const UROLOGY_FIELDS = D([["chief_complaints", "Chief Complaints"], ["associated_symptoms", "Associated Symptoms"]]);

const DEPARTMENT_FIELDS = {
    CARDIOLOGY: D([["chest_pain_type", "Chest Pain Type (Typical/Atypical)"], ["onset_duration", "Onset & Duration"], ["palpitation", "Palpitation"], ["dyspnea", "Breathlessness (Dyspnea)"], ["syncope", "Syncope"], ["ecg_findings", "ECG Findings"], ["echo_findings", "ECHO Findings"], ["risk_factors", "Risk Factors (DM/HTN/Smoking)"], ["indication", "Indication (Reason for Procedure)"], ["procedure_type", "Procedure Type (Angiogram/Angioplasty)"], ["access_site", "Access Site (Radial/Femoral)"], ["vessel_involved", "Vessel Involved (LAD/RCA/LCX)"], ["lesion_details", "Lesion Details"], ["stent_used", "Stent Used (Type/Size)"], ["balloon_used", "Balloon Used"], ["complications", "Complications"], ["contrast_volume", "Contrast Volume Used (ml)"], ["fluoro_time", "Fluoroscopy Time"], ["hemodynamics", "Hemodynamics (BP/HR)"], ["post_procedure_plan", "Post Procedure Plan"], ["pre_procedure_diagnosis", "Pre-Procedure Diagnosis"], ["post_procedure_diagnosis", "Post-Procedure Diagnosis"], ["closure_device", "Closure Device Used"], ["anticoagulation", "Anticoagulation Given"]]),
    ORTHOPAEDICS: D([["joint_pain_site", "Joint Pain Site"], ["pain_duration", "Pain Duration"], ["swelling", "Swelling"], ["tenderness", "Tenderness"], ["range_of_motion", "Range of Motion"], ["fracture_history", "Fracture History"], ["deformity", "Deformity"], ["implant_history", "Implant / Surgery History"]]),
    PSYCHIATRIS: D([["mood", "Mood"], ["affect", "Affect"], ["thought_content", "Thought Content"], ["hallucination", "Hallucination"], ["sleep_pattern", "Sleep Pattern"], ["appetite", "Appetite"], ["suicidal_ideas", "Suicidal Ideation"], ["insight", "Insight & Judgment"]]),
    DERMATOLOGY: D([["lesion_type", "Lesion Type"], ["site_distribution", "Site / Distribution"], ["itching", "Itching"], ["duration", "Duration"], ["pigmentation", "Pigmentation Changes"], ["infection_signs", "Infection Signs"], ["allergy_history", "Allergy History"]]),
    GASTROENTRO_SURGERY: D([["abdominal_pain_site", "Abdominal Pain Site"], ["pain_character", "Pain Character"], ["vomiting", "Vomiting"], ["bowel_changes", "Bowel Habit Changes"], ["bleeding_pr", "Bleeding PR"], ["weight_loss", "Weight Loss"], ["endoscopy_findings", "Endoscopy Findings"]]),
    UROLOGY: UROLOGY_FIELDS,
    NEPHROLOGY: UROLOGY_FIELDS,
    GENERAL_MEDICINE: UROLOGY_FIELDS,
    GENERAL_SURGERY: D([["lump_site", "Lump Site"], ["lump_size", "Lump Size"], ["tenderness", "Tenderness"], ["reducibility", "Reducibility (Hernia)"], ["cough_impulse", "Cough Impulse"], ["previous_surgery", "Previous Surgery History"]]),
    ENT: D([["ear_discharge", "Ear Discharge"], ["hearing_loss", "Hearing Loss"], ["nasal_block", "Nasal Block"], ["throat_pain", "Throat Pain"], ["voice_change", "Voice Change"], ["tinnitus", "Tinnitus"]]),
    PLASTIC_SURGERY: D([["scar_type", "Scar Type"], ["burn_degree", "Burn Degree"], ["wound_status", "Wound Status"], ["cosmetic_concern", "Cosmetic Concern"], ["reconstruction_need", "Reconstruction Need"]]),
    PULMONOLOGY: D([["cough_duration", "Cough Duration"], ["sputum", "Sputum"], ["breathlessness", "Breathlessness"], ["wheezing", "Wheezing"], ["smoking_history", "Smoking History"], ["chest_exam", "Chest Examination"]]),
    NEUROLOGY: D([["headache_type", "Headache Type"], ["seizure_history", "Seizure History"], ["weakness", "Weakness"], ["sensory_loss", "Sensory Loss"], ["speech_issue", "Speech Issue"], ["gait", "Gait"]]),
    GYNAEC_OBSTETRIC: D([["menstrual_history", "Menstrual History"], ["obstetric_history", "Obstetric History"], ["pregnancy_status", "Pregnancy Status"], ["pv_bleeding", "PV Bleeding"], ["discharge", "Discharge"], ["contraceptive_use", "Contraceptive Use"]]),
    PAEDIATRIC: D([["fever_duration", "Fever Duration"], ["feeding", "Feeding"], ["growth_development", "Growth & Development"], ["immunization", "Immunization Status"], ["activity", "Activity Level"]]),
};

const REMARK_OPTIONS = ["1-0-1", "1-1-1", "0-1-1", "0-1-0", "0-0-1", "1-0-0", "1-1-0", "1/2-0-1", "1-1/2-1", "1/2-1/2-1"];
const FREQ_OPTIONS = ["After Food", "Before Food", "30 Min Before Food", "15 Min Before Food", "Weekly Once", "Monthly Once", "HS", "HIS"];

/* =====================================================================
   DENTAL CHART GEOMETRY (unchanged maths)
   ===================================================================== */

const buildArch = (cx, cy, rx, ry, startDeg, p1, p2) =>
    Array.from({length: 16}, (_, i) => {
        const angle = (startDeg + (i * 140) / 15) * (Math.PI / 180);
        const x = cx + rx * Math.cos(angle);
        const y = cy + ry * Math.sin(angle);
        const first = i < 8;
        return {id: `${first ? p1 : p2}Q${first ? 8 - i : i - 7}`, x: Math.round(x), y: Math.round(y), label: first ? 8 - i : i - 7};
    });
const UPPER_TEETH = buildArch(410, -10, 170, -150, -160, "U1", "U2");
const LOWER_TEETH = buildArch(410, 450, 170, -150, 20, "L3", "L4");

/* =====================================================================
   HELPERS
   ===================================================================== */

const uid = (() => {
    let n = 0;
    return () => `r${++n}`;
})();

const blankDrug = () => ({key: uid(), id: "", remarks: "", freq: "", ddays: "", dose: "", dnotes: "", pastName: ""});
const blankOrder = (key) => ({key: key || uid(), id: "", name: "", amount: 0});

const splitCsv = (v) => String(v ?? "").split(",").map((s) => s.trim());
const clean = (v) => String(v ?? "").replace(/[,;]+/g, " ").trim();
const joinText = (base, spoken) => {
    if (!spoken) return base || "";
    return (base ? base.replace(/\s+$/, "") + " " : "") + spoken;
};

const formatLocalDate = (date) => {
    if (!date) return "";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};
const parseLocalDate = (s) => {
    if (!s) return null;
    const [y, m, d] = String(s).split("-").map(Number);
    return new Date(y, m - 1, d);
};

const parseDept = (v) => {
    if (!v) return {};
    if (typeof v === "object") return v;
    try {
        return JSON.parse(v) || {};
    } catch {
        return {};
    }
};
const CASE_KEYS = ["drug_id", "diagnosis", "complaints", "illness", "allergy", "tsugg", "tadvise", "diet", "physio", "examination", "department_data", "teeth_notes", "symptom", "chief_complaints", "med_history"];
const caseScore = (o) => CASE_KEYS.filter((k) => o?.[k] !== null && o?.[k] !== undefined && String(o[k]).trim() !== "" && String(o[k]) !== "{}").length;

/* finds the consultform record (the one with the most data) whether it is the visit itself or nested inside it */
const findCase = (root) => {
    let best = null, bestScore = 0;
    const walk = (v, depth) => {
        if (!v || typeof v !== "object" || depth > 4) return;
        if (Array.isArray(v)) {
            v.forEach((x) => walk(x, depth + 1));
            return;
        }
        const s = caseScore(v);
        if (s > bestScore) {
            best = v;
            bestScore = s;
        }
        Object.keys(v).forEach((k) => walk(v[k], depth + 1));
    };
    walk(root, 0);
    return best;
};

/* join with commas, then drop empty trailing commas: "a,b,,"  ->  "a,b" */
const joinTrim = (arr) => arr.map((x) => (x === null || x === undefined ? "" : String(x))).join(",").replace(/(\s*,)+\s*$/, "");
const parseFraction = (part) => {
    part = String(part).trim();
    if (!part) return 0;
    if (part.includes("/")) {
        const [a, b] = part.split("/").map(parseFloat);
        return !isNaN(a) && !isNaN(b) && b !== 0 ? a / b : 0;
    }
    const n = parseFloat(part);
    return isNaN(n) ? 0 : n;
};
/* 1-0-1 x 5 days = 10 ; 1/2-0-1 x 4 = 6 */
const calcQty = (remarks, ddays) => {
    const perDay = String(remarks || "").split("-").reduce((s, p) => s + parseFraction(p), 0);
    const days = parseFloat(ddays);
    return perDay > 0 && !isNaN(days) && days > 0 ? Math.round(perDay * days) : "";
};

const rowsFromRecord = (rec) => {
    const ids = splitCsv(rec.drug_id), rem = splitCsv(rec.remarks), fq = splitCsv(rec.freq);
    const dd = splitCsv(rec.ddays), ds = splitCsv(rec.dose), nt = splitCsv(rec.dnotes);
    return ids
        .map((id, i) => ({id, i}))
        .filter((x) => x.id)
        .map(({id, i}) => ({key: uid(), id, remarks: rem[i] || "", freq: fq[i] || "", ddays: dd[i] || "", dose: ds[i] || "", dnotes: nt[i] || "", pastName: ""}));
};

const parseTeethNotes = (str) => {
    const selected = [], data = {};
    String(str || "").split(";").forEach((seg) => {
        const c = seg.indexOf(":");
        if (!seg.trim() || c < 0) return;
        const id = seg.slice(0, c).trim();
        const t = {hard: {}, soft: {}};
        seg.slice(c + 1).split(",").forEach((p) => {
            const k = p.indexOf(":");
            if (k < 0) return;
            const key = p.slice(0, k).trim(), val = p.slice(k + 1).trim();
            if (HARD_FIELDS.includes(key)) t.hard[key] = val;
            else if (SOFT_FIELDS.includes(key)) t.soft[key] = val;
        });
        selected.push(id);
        data[id] = t;
    });
    return {selected, data};
};

const parseSavedPoints = (coordinatesStr, textmapStr) => {
    const labels = textmapStr.split(",").map((l) => l.trim());
    return coordinatesStr.split(",").map((c, i) => {
        const [x, y, z] = c.split(":").map(Number);
        return {id: `${Date.now()}-${i}`, position: [x, y, z], label: labels[i] || ""};
    });
};

const visitDate = (v) => v.add_date || v.created_at || v.created || v.appt?.appt_date || v.date || "";
const toast = (title, icon = "success") => Swal.fire({title, icon, timer: 2000, showConfirmButton: false});

const FORM_KEYS = ["weight", "height", "bmi", "tempr", "pulse", "bp", "spo2", "sugar", "allergy", "illness", "complaints", "diagnosis", "tsugg", "tadvise", "diet", "physio", "examination", "review_days", "review_date", "pat_type", "duration_tt", "otherdrugs", "history", "pregnant"];
const SAVE_KEYS = ["weight", "height", "bmi", "tempr", "pulse", "bp", "spo2", "sugar", "allergy", "illness", "complaints", "diagnosis", "tsugg", "tadvise", "diet", "physio", "review_days", "pat_type", "duration_tt", "examination"];
const emptyForm = () => ({...Object.fromEntries(FORM_KEYS.map((k) => [k, ""])), symptomChoice: "", otherSymptom: ""});

const ORDER_CFG = {
    lab: {numberUrl: "get_appt_lab_number/", createUrl: "lab-create/", ids: "ltest_id", names: "lab_test", amounts: "lab_amount", tot: "lab_tot", disc: "lab_disc", status: "lab_status", linkField: "lab_id", emptyTitle: "Select Lab Test", emptyText: "Please select at least one lab test.", done: "Lab entry has been added."},
    inv: {numberUrl: "get_appt_inv_number/", createUrl: "inv-create/", ids: "itest_id", names: "inv_test", amounts: "inv_amount", tot: "inv_tot", disc: "inv_disc", status: "inv_status", linkField: "inv_id", emptyTitle: "Select Radiology Test", emptyText: "Please select at least one radiology test.", done: "Radiology entry has been added."},
    min: {numberUrl: "get_appt_min_number/", createUrl: "min-create/", ids: "min_id", names: "min_test", amounts: "min_amount", tot: "min_tot", disc: "min_disc", status: "min_status", linkField: null, emptyTitle: "Select Treatment", emptyText: "Please select at least one MIN treatment.", done: "Min. treatment entry has been added."},
};

/* =====================================================================
   SMALL COMPONENTS  (defined OUTSIDE the page so they never remount
   while typing - this is what keeps focus in the text areas)
   ===================================================================== */

const Marker = React.memo(function Marker({point, onLabelChange}) {
    const [localLabel, setLocalLabel] = useState(point.label || "");
    useEffect(() => setLocalLabel(point.label || ""), [point.label]);
    return (
        <mesh position={point.position}>
            <sphereGeometry args={[0.05, 16, 16]}/>
            <meshStandardMaterial color="red"/>
            <Html distanceFactor={10} style={{pointerEvents: "auto"}}>
                <input type="text" placeholder="Enter label" value={localLabel}
                       onChange={(e) => setLocalLabel(e.target.value)}
                       onBlur={() => onLabelChange(point.id, localLabel)}
                       onFocus={(e) => e.stopPropagation()}
                       style={{padding: 2, fontSize: 12, width: 100, outline: "none"}}/>
                <div style={{fontSize: 10, color: "blue", marginTop: 4}}>
                    x: {point.position[0].toFixed(2)}, y: {point.position[1].toFixed(2)}, z: {point.position[2].toFixed(2)}
                </div>
            </Html>
        </mesh>
    );
});

function SectionTitle({icon, children, right}) {
    return (
        <div className="cd-section-title">
            <span><i className={`fa-solid ${icon}`}/>{children}</span>
            {right}
        </div>
    );
}

function NoteField({label, name, rows = 5, value, onChange, active, onStart, onStop, supported, col = "col-xl-4 col-lg-6 col-md-6"}) {
    return (
        <div className={`${col} mb-4`}>
            <div className={`field-card h-100 ${active ? "is-listening" : ""}`}>
                <label className="form-label fw-semibold">
                    <i className="fa-solid fa-file-medical text-success me-2"/>{label}
                    {active && <span className="cd-live">● listening</span>}
                </label>
                <textarea className="form-control no-comma" rows={rows} value={value} onChange={(e) => onChange(name, e.target.value)}/>
                <div className="cd-mic">
                    <button type="button" tabIndex={-1} className="btn btn-outline-danger" disabled={!supported} onClick={onStop}>
                        <i className="fa-solid fa-microphone-lines-slash"/>
                    </button>
                    <button type="button" tabIndex={-1} className="btn btn-outline-success" disabled={!supported} onClick={() => onStart(name)}>
                        <i className="fa-solid fa-microphone-lines"/>
                    </button>
                </div>
            </div>
        </div>
    );
}

function useOrderRows(labelKey) {
    const [rows, setRows] = useState([blankOrder()]);
    const select = (i, val) => setRows((p) => p.map((r, x) => x !== i ? r : val
        ? {...r, id: val.id, name: val[labelKey] || "", amount: Number(val.amount) || 0}
        : blankOrder(r.key)));
    const add = () => setRows((p) => [...p, blankOrder()]);
    const remove = (i) => setRows((p) => p.length > 1 ? p.filter((_, x) => x !== i) : [blankOrder()]);
    const reset = () => setRows([blankOrder()]);
    const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    return {rows, setRows, select, add, remove, reset, total};
}

function OrderTab({title, nameHeader, items, labelKey, state, onSubmit, submitting, viewTitle, viewRows, viewNameKey, statusBadge}) {
    const label = (o) => String(o?.[labelKey] ?? "");
    return (
        <>
            <SectionTitle icon="fa-list-check">{title}</SectionTitle>
            <div className="table-responsive">
                <table className="erp-table-modern cd-table">
                    <thead>
                    <tr><th style={{width: 70}}>ID</th><th>{nameHeader}</th><th style={{width: 140}}>Amount</th><th style={{width: 110}}>Action</th></tr>
                    </thead>
                    <tbody>
                    {state.rows.map((row, i) => (
                        <tr key={row.key}>
                            <td><input className="form-control" type="text" value={row.id} readOnly tabIndex={-1}/></td>
                            <td>
                                <AutoComplete
                                    options={items}
                                    value={items.find((it) => String(it?.id) === String(row.id)) || null}
                                    isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                                    getOptionLabel={label}
                                    onChange={(e, val) => state.select(i, val)}
                                    clearOnEscape
                                    handleHomeEndKeys
                                    renderOption={(props, option) => {
                                        const {key, ...rest} = props;
                                        return (
                                            <li key={option?.id ?? key} {...rest}>
                                                <div><strong>{label(option)}</strong>
                                                    {option?.amount !== undefined && <small className="d-block text-muted">₹ {option.amount}</small>}
                                                </div>
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <div ref={params.InputProps.ref}>
                                            <input className="form-control cd-ac-input" type="text" {...params.inputProps}/>
                                        </div>
                                    )}
                                />
                            </td>
                            <td><input className="form-control" type="number" value={row.amount} readOnly tabIndex={-1}/></td>
                            <td><button className="btn btn-outline-danger" type="button" onClick={() => state.remove(i)}><i className="fa-solid fa-trash-can"/></button></td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            <div className="cd-order-foot">
                <button className="btn btn-outline-success" type="button" onClick={state.add}><i className="fa-solid fa-plus me-2"/>Add More</button>
                <span className="cd-total">Total <b>₹ {state.total}</b></span>
                <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={submitting}>
                    <i className="fa-solid fa-paper-plane me-2"/>{submitting ? "Sending..." : "Submit"}
                </button>
            </div>

            {viewRows && (
                <>
                    <SectionTitle icon="fa-clock-rotate-left">{viewTitle}</SectionTitle>
                    <div className="table-responsive">
                        <table className="erp-table-modern cd-table">
                            <thead><tr><th>Appt ID</th><th>Test Name</th><th>Addedby</th><th>Action</th></tr></thead>
                            <tbody>
                            {viewRows.map((v, i) => (
                                <tr key={v.id ?? i}><td>{v.appt_id}</td><td>{v[viewNameKey]}</td><td>{v.addedby}</td><td>{statusBadge(v)}</td></tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </>
    );
}

/* =====================================================================
   PAGE
   ===================================================================== */

function ConsDetail() {
    const {appt_id} = useParams();

    /* ----- server data ----- */
    const [appointment, setAppointment] = useState([]);
    const [departments, setDepartments] = useState("");
    const [conData, setConData] = useState([]);
    const [labview, setLabView] = useState([]);
    const [invview, setInvView] = useState([]);
    const [medview, setMedView] = useState([]);
    const [drugitems, setDrugItems] = useState([]);
    const [labitems, setLabItems] = useState([]);
    const [invitems, setInvItems] = useState([]);
    const [minitems, setMinItems] = useState([]);

    /* ----- the ONE editable state for the case sheet ----- */
    const [form, setForm] = useState(emptyForm);
    const [deptData, setDeptData] = useState({});
    const [drugRows, setDrugRows] = useState([blankDrug()]);
    const [drugNames, setDrugNames] = useState({});
    const [chief, setChief] = useState({});
    const [medFlags, setMedFlags] = useState({});
    const [selectedTooth, setSelectedTooth] = useState([]);
    const [toothData, setToothData] = useState({});
    const [selectedDate, setSelectedDate] = useState(null);
    const [ipStatus, setIpStatus] = useState(0);

    /* ----- misc ----- */
    const [tab, setTab] = useState("case");
    const [saving, setSaving] = useState(false);
    const [refilling, setRefilling] = useState(false);
    const [submittingOrder, setSubmittingOrder] = useState("");
    const [points, setPoints] = useState([]);
    const [medicalDate, setMedicalDate] = useState(new Date());
    const [medFile, setMedFile] = useState({ftype: "ctscan", comments: "", file: null});
    const [favs, setFavs] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(FAV_KEY)) || [];
        } catch {
            return [];
        }
    });
    const [showFavs, setShowFavs] = useState(false);

    const lab = useOrderRows("subcat");
    const inv = useOrderRows("components");
    const min = useOrderRows("comp");
    const orderState = {lab, inv, min};

    const hydratedRef = useRef(null);
    const triedNamesRef = useRef(new Set());
    const synthRef = useRef(new Map());
    const focusKeyRef = useRef(null);
    const baseRef = useRef("");

    const a = appointment[0];
    const deptKey = String(departments || "").replace(/\s+/g, "_").toUpperCase();
    const isDental = departments === "DENTAL";

    /* =================================================================
       FETCHING
       ================================================================= */

    // A consultation record may not exist yet for a newly opened appointment.
    // Do not call this with APID-... or with an emr-op/op-bill row's own PK.
    const fetchConData = useCallback(async (appointmentRef, {quietNotFound = true} = {}) => {
        const appointmentPk = numericId(appointmentRef);
        if (!appointmentPk) {
            setConData([]);
            setPoints([]);
            return [];
        }
        try {
            const res = await axios.get(`${baseUrl}view-consultform/${appointmentPk}`, {
                params: {location: userLocation},
            });
            const records = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
            setConData(records);
            const record = records[0];
            setPoints(record?.coordinates && record?.textmap ? parseSavedPoints(record.coordinates, record.textmap) : []);
            return records;
        } catch (err) {
            // 404 means this appointment has no case sheet yet. That's normal on first open.
            if (!(quietNotFound && err.response?.status === 404)) {
                console.error("Failed to fetch consultation data for appointment:", appointmentPk, err);
            }
            setConData([]);
            setPoints([]);
            return [];
        }
    }, []);

    const fetchInv = useCallback(() => axios.get(`${baseUrl}view-inv/${appt_id}`).then((r) => setInvView(r.data)).catch(console.log), [appt_id]);
    const fetchLab = useCallback(() => axios.get(`${baseUrl}view-lab-op/${appt_id}`).then((r) => setLabView(r.data)).catch(console.log), [appt_id]);
    const fetchFiles = useCallback(() => axios.get(`${baseUrl}view-files/${appt_id}`).then((r) => setMedView(r.data)).catch(console.log), [appt_id]);

    useEffect(() => {
        if (!appt_id) return;
        axios.get(`${baseUrl}view-appointment/${appt_id}`).then((r) => {
            setAppointment(r.data);
            if (r.data.length > 0 && r.data[0].cons) setDepartments(r.data[0].cons.department);
        }).catch(console.log);
        axios.get(`${baseUrl}indent-pharm-search/?dept=Pharmacy`).then((r) => setDrugItems(r.data)).catch(console.log);
        axios.get(`${baseUrl}lab-search/`).then((r) => setLabItems(r.data)).catch(console.log);
        axios.get(`${baseUrl}inv-search/`).then((r) => setInvItems(r.data)).catch(console.log);
        axios.get(`${baseUrl}min-search/`).then((r) => setMinItems(r.data)).catch(console.log);
        fetchInv();
        fetchLab();
        fetchFiles();
    }, [appt_id, fetchInv, fetchLab, fetchFiles]);

    // Resolve the actual appointment FK from the appointment response. In some
    // responses `id` belongs to an OP-bill/EMR row, so never blindly use a.id.
    const consultationAppointmentId = (() => {
    const id = String(appt_id ?? "").trim();

    if (!/^\d+$/.test(id)) return null;

    const numericId = Number(id);

    return Number.isSafeInteger(numericId) && numericId > 0
        ? numericId
        : null;
})();
    useEffect(() => {
        if (!consultationAppointmentId) {
            setConData([]);
            setPoints([]);
            return;
        }
        fetchConData(consultationAppointmentId);
    }, [consultationAppointmentId, fetchConData]);

    /* =================================================================
       HYDRATE THE FORM FROM THE SAVED RECORD
       Runs once per saved consultation (and again right after a save).
       After that the form is the only source of truth, so typing and
       deleting medicines can never be overwritten by server data.
       ================================================================= */

    useEffect(() => {
        if (!conData.length) return;
        const rec = conData[0];
        const key = String(rec.id);
        if (hydratedRef.current === key) return;
        hydratedRef.current = key;

        const next = emptyForm();
        FORM_KEYS.forEach((k) => {
            if (rec[k] !== null && rec[k] !== undefined) next[k] = String(rec[k]);
        });
        next.pregnant = rec.ispregnant != null ? String(rec.ispregnant) : "";
        const sym = String(rec.symptom ?? "").trim();
        next.symptomChoice = !sym ? "" : SYMPTOMS.includes(sym) ? sym : "OTHER";
        next.otherSymptom = sym && !SYMPTOMS.includes(sym) ? sym : "";
        setForm(next);
        setDeptData(parseDept(rec.department_data));
        setSelectedDate(rec.review_date ? parseLocalDate(rec.review_date) : null);
        setIpStatus(Number(rec.ip_status) || 0);

        const toFlags = (csv) => Object.fromEntries(splitCsv(csv).filter(Boolean).map((k) => [k, true]));
        setChief(toFlags(rec.chief_complaints));
        setMedFlags(toFlags(rec.med_history));

        const teeth = parseTeethNotes(rec.teeth_notes);
        setSelectedTooth(teeth.selected);
        setToothData(teeth.data);

        const rows = rowsFromRecord(rec);
        setDrugRows(rows.length ? rows : [blankDrug()]);
    }, [conData]);

    /* names of saved medicines that are no longer in the live stock list */
    useEffect(() => {
        const ids = new Set();
        drugRows.forEach((r) => r.id && ids.add(String(r.id)));
        conData.forEach((rec) => splitCsv(rec.drug_id).filter(Boolean).forEach((id) => ids.add(id)));
        ids.forEach((id) => {
            if (drugNames[id] || triedNamesRef.current.has(id)) return;
            if (drugitems.some((it) => String(it.id) === id)) return;
            if (!drugitems.length) return; // wait for the stock list first
            triedNamesRef.current.add(id);
            axios.get(`${baseUrl}update-indent-use/${id}/`)
                .then((r) => setDrugNames((p) => ({...p, [id]: r.data?.product_name || ""})))
                .catch(console.log);
        });
    }, [drugRows, conData, drugitems, drugNames]);

    const nameForId = (id) => {
        const it = drugitems.find((x) => String(x.id) === String(id));
        return it?.product?.product || drugNames[String(id)] || "";
    };

    /* optional: preload saved lab / radiology requests */
    useEffect(() => {
        if (!PREFILL_SAVED_ORDERS) return;
        const build = (view, items, idKey, labelKey, nameKey, amtKey, st) => {
            if (!view.length || !items.length) return;
            const rows = [];
            view.forEach((rec) => {
                const ids = splitCsv(rec[idKey]).filter(Boolean), names = splitCsv(rec[nameKey]), amts = splitCsv(rec[amtKey]);
                ids.forEach((id, i) => {
                    const o = items.find((x) => String(x.id) === id);
                    rows.push({key: uid(), id, name: o?.[labelKey] ?? names[i] ?? "", amount: Number(o?.amount ?? amts[i] ?? 0) || 0});
                });
            });
            if (rows.length) st.setRows([...rows, blankOrder()]);
        };
        build(labview, labitems, "ltest_id", "subcat", "lab_test", "lab_amount", lab);
        build(invview, invitems, "itest_id", "components", "inv_test", "inv_amount", inv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labview, labitems, invview, invitems]);

    /* =================================================================
       FORM HANDLERS
       ================================================================= */

    const getVal = (k) => (k.startsWith("dept:") ? deptData[k.slice(5)] ?? "" : form[k] ?? "");
    const setVal = (k, v) => (k.startsWith("dept:")
        ? setDeptData((p) => ({...p, [k.slice(5)]: v}))
        : setForm((p) => ({...p, [k]: v})));

    const inputHandler = (e) => {
        const {name, value} = e.target;
        setForm((prev) => {
            const u = {...prev, [name]: value};
            if (name === "weight" || name === "height") {
                const w = parseFloat(u.weight), h = parseFloat(u.height);
                u.bmi = !isNaN(w) && !isNaN(h) && h > 0 ? (w / ((h / 100) ** 2)).toFixed(2) : "";
            }
            return u;
        });
    };

    const handleReviewDaysChange = (e) => {
        const value = e.target.value;
        if (value === "") {
            setSelectedDate(null);
            setForm((p) => ({...p, review_days: "", review_date: ""}));
            return;
        }
        const days = parseInt(value, 10);
        if (isNaN(days) || days < 0) {
            setForm((p) => ({...p, review_days: value}));
            return;
        }
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + days);
        setSelectedDate(d);
        setForm((p) => ({...p, review_days: value, review_date: formatLocalDate(d)}));
    };

    const handleDateChange = (date) => {
        setSelectedDate(date || null);
        setForm((p) => ({...p, review_date: date ? formatLocalDate(date) : ""}));
    };

    /* ----- teeth ----- */
    const toggleTooth = (id) => setSelectedTooth((p) => (p.includes(id) ? p.filter((t) => t !== id) : [...p, id]));
    const updateTooth = (id, group, field, value) => setToothData((p) => ({
        ...p, [id]: {...p[id], [group]: {...(p[id]?.[group] || {}), [field]: value}},
    }));

    /* =================================================================
       VOICE
       ================================================================= */

    const {transcript, resetTranscript, browserSupportsSpeechRecognition} = useSpeechRecognition({continuous: true});
    const [activeField, setActiveField] = useState(null);

    useEffect(() => {
        if (!activeField) return;
        setVal(activeField, joinText(baseRef.current, transcript));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transcript, activeField]);

    useEffect(() => () => SpeechRecognition.stopListening(), []);

    const startListening = (key) => {
        baseRef.current = getVal(key);
        resetTranscript();
        setActiveField(key);
        SpeechRecognition.startListening({continuous: true, interimResults: true, language: "en-IN"});
    };
    const stopListening = () => {
        SpeechRecognition.stopListening();
        setActiveField(null);
    };
    const onNoteChange = (key, value) => {
        if (key === activeField) {
            baseRef.current = value;
            resetTranscript();
        }
        setVal(key, value);
    };
    const noteProps = (name) => ({
        name, value: getVal(name), onChange: onNoteChange, active: activeField === name,
        onStart: startListening, onStop: stopListening, supported: browserSupportsSpeechRecognition,
    });

    /* =================================================================
       MEDICINES
       ================================================================= */

    const updateDrugField = (i, name, value) => setDrugRows((p) => p.map((r, x) => {
        if (x !== i) return r;
        const n = {...r, [name]: value};
        if (name === "remarks" || name === "ddays") n.dose = calcQty(n.remarks, n.ddays);
        return n;
    }));

    const selectDrug = (i, val) => setDrugRows((p) => p.map((r, x) => (x !== i ? r : val
        ? {...r, id: val.id, pastName: ""}
        : {...r, id: "", pastName: ""})));

    const addDrugRow = () => {
        const r = blankDrug();
        focusKeyRef.current = r.key;
        setDrugRows((p) => [...p, r]);
    };
    const removeDrugRow = (i) => setDrugRows((p) => (p.length > 1 ? p.filter((_, x) => x !== i) : [blankDrug()]));

    useEffect(() => {
        if (!focusKeyRef.current) return;
        document.querySelector(`[data-drug-key="${focusKeyRef.current}"]`)?.focus();
        focusKeyRef.current = null;
    }, [drugRows]);

    const onDrugKeyDown = (e, i) => {
        if (e.key === "Shift" && !e.repeat && i === drugRows.length - 1 && drugRows[i].id) {
            e.preventDefault();
            addDrugRow();
        } else if (e.key === "Alt" && drugRows.length > 1) {
            e.preventDefault();
            removeDrugRow(i);
        }
    };

    const optionFor = (row) => {
        if (!row.id) return null;
        const live = drugitems.find((it) => String(it.id) === String(row.id));
        if (live) return live;
        const name = drugNames[String(row.id)] || `#${row.id}`;
        const cached = synthRef.current.get(String(row.id));
        if (cached && cached.product.product === name) return cached;
        const obj = {id: row.id, product: {product: name}, synthetic: true};
        synthRef.current.set(String(row.id), obj);
        return obj;
    };

    const drugFilter = (options, state) => {
        const q = state.inputValue.toLowerCase().trim();
        if (q.length < 3) return [];
        return options.filter((o) =>
            (o.product?.product || "").toLowerCase().trim().startsWith(q) ||
            (o.product?.generic || "").toLowerCase().trim().startsWith(q));
    };

    /* ----- favourites (stored in this browser, per user) ----- */
    const persistFavs = (list) => {
        setFavs(list);
        try {
            localStorage.setItem(FAV_KEY, JSON.stringify(list));
        } catch {/* storage blocked */}
    };
    const isFav = (row) => {
        const n = nameForId(row.id).toLowerCase();
        return !!n && favs.some((f) => f.name.toLowerCase() === n);
    };
    const toggleFav = (row) => {
        const name = nameForId(row.id);
        if (!name) return;
        if (isFav(row)) persistFavs(favs.filter((f) => f.name.toLowerCase() !== name.toLowerCase()));
        else persistFavs([...favs, {name, remarks: row.remarks, freq: row.freq, ddays: row.ddays, dnotes: row.dnotes}]);
    };
    const addFavToRows = (fav) => {
        const stock = drugitems.find((it) => (it.product?.product || "").toLowerCase() === fav.name.toLowerCase());
        const row = {
            ...blankDrug(), id: stock ? stock.id : "", pastName: stock ? "" : fav.name,
            remarks: fav.remarks || "", freq: fav.freq || "", ddays: fav.ddays || "", dnotes: fav.dnotes || "",
            dose: calcQty(fav.remarks, fav.ddays),
        };
        setDrugRows((p) => {
            const last = p[p.length - 1];
            const empty = last && !last.id && !last.pastName && !last.remarks && !last.ddays;
            return empty ? [...p.slice(0, -1), row] : [...p, row];
        });
        if (!stock) toast(`${fav.name} is not in current stock - pick a substitute`, "warning");
    };

        /* ----- AUTO REFILL from a previous visit ----- */

const applyVisit = async (visit, mode = "clinical") => {
    // Do not trust findCase(visit): this row may belong to opbillamt.
    let v = null;
    let appointmentPk = null;

    const publicAppointmentRef = getPublicAppointmentRef(visit);

    console.log("AUTO REFILL: selected visit", visit);
    console.log("AUTO REFILL: public appointment reference", publicAppointmentRef);

    try {
        if (publicAppointmentRef) {
            // Step 1: Resolve APID-xxxxx to the real Appointment record.
            console.log("AUTO REFILL: requesting view-appt");

            const resolveResponse = await axios.get(
                `${baseUrl}view-appt/${encodeURIComponent(publicAppointmentRef)}/`,
                {
                    params: { location: userLocation },
                }
            );

            console.log(
                "AUTO REFILL: view-appt response",
                resolveResponse.data
            );

            const resolvedRows = responseRows(resolveResponse.data);

            // Prefer an exact public-reference match if the endpoint returns
            // more than one row.
            const resolvedAppointment =
                resolvedRows.find((row) =>
                    String(row.appt_id ?? "").trim() === publicAppointmentRef
                ) || resolvedRows[0];

            appointmentPk = numericId(
                resolvedAppointment?.id ?? resolvedAppointment?.pk
            );
        }

        // Fallback: use only explicit appointment FK fields.
        // Never use visit.id, because it may be the OP-bill primary key.
        if (!appointmentPk) {
            appointmentPk = getConsultAppointmentId(visit);
        }

        if (!appointmentPk) {
            console.error(
                "AUTO REFILL STOPPED: no numeric Appointment.id found",
                {
                    publicAppointmentRef,
                    visit,
                }
            );

            await Swal.fire(
                "Appointment ID not found",
                "Could not resolve this previous visit to its Appointment primary key. Check the selected visit data and view-appt API response.",
                "warning"
            );
            return;
        }

        console.log("AUTO REFILL: numeric Appointment.id =", appointmentPk);

        // Step 2: Verify the appointment API using the numeric primary key.
        const appointmentResponse = await axios.get(
            `${baseUrl}view-appointment/${appointmentPk}`,
            {
                params: { location: userLocation },
            }
        );

        console.log(
            "AUTO REFILL: view-appointment response",
            appointmentResponse.data
        );

        // Step 3: Load the actual saved OpConsultForm.
        const consultResponse = await axios.get(
            `${baseUrl}view-consultform/${appointmentPk}`,
            {
                params: { location: userLocation },
            }
        );

        console.log(
            "AUTO REFILL: view-consultform response",
            consultResponse.data
        );

        const consultRows = responseRows(consultResponse.data);

        // Prefer the saved consultation record explicitly linked to this
        // appointment. Do not accept an arbitrary object from emr-op.
        v = consultRows.find((row) => {
            const linkedId = numericId(
                row.appt ??
                row.appointment_id ??
                row.appointment?.id
            );

            return linkedId === String(appointmentPk);
        });

        // Some serializers return the consultation form without the
        // appointment FK field. Accept its first record if it has case data.
        if (!v) {
            v = findCase(consultRows);
        }

        if (!v || caseScore(v) === 0) {
            console.warn(
                "AUTO REFILL: no usable saved case-sheet fields found",
                {
                    appointmentPk,
                    response: consultResponse.data,
                }
            );

            await Swal.fire(
                "Nothing to fill",
                `No usable saved case sheet was returned for Appointment ID ${appointmentPk}. Check the view-consultform response in the console.`,
                "info"
            );
            return;
        }

        console.log("AUTO REFILL: saved case sheet resolved", v);

    } catch (err) {
        console.error("AUTO REFILL API ERROR", {
            publicAppointmentRef,
            appointmentPk,
            url: err.config?.url,
            status: err.response?.status,
            response: err.response?.data,
            message: err.message,
        });

        await Swal.fire(
            "Could not load previous case sheet",
            err.response?.data?.detail ||
            err.response?.data?.message ||
            `Request failed (${err.response?.status || "network error"}). Check the browser console and Network tab.`,
            "error"
        );
        return;
    }



        /* ----- medicines: only the dedicated drug refill loads these ----- */
        let missing = [];
        if (mode === "drugs" || mode === "all") {
        const ids = splitCsv(v.drug_id);
        const names = await Promise.all(ids.map((id) => (id
            ? axios.get(`${baseUrl}update-indent-use/${id}/`).then((r) => r.data?.product_name || "").catch(() => "")
            : Promise.resolve(""))));
        const rem = splitCsv(v.remarks), fq = splitCsv(v.freq), dd = splitCsv(v.ddays), ds = splitCsv(v.dose), nt = splitCsv(v.dnotes);
        const rows = ids.map((id, i) => ({id, i})).filter((x) => x.id).map(({id, i}) => {
            const name = names[i] || nameForId(id);
            const stock = drugitems.find((it) => String(it.id) === String(id))
                || (name && drugitems.find((it) => (it.product?.product || "").toLowerCase() === name.toLowerCase()));
            if (!stock) missing.push(name || `#${id}`);
            return {
                key: uid(), id: stock ? stock.id : "", pastName: stock ? "" : (name || `#${id}`),
                remarks: rem[i] || "", freq: fq[i] || "", ddays: dd[i] || "",
                dose: calcQty(rem[i], dd[i]) || ds[i] || "", dnotes: nt[i] || "",
            };
        });
        if (rows.length) setDrugRows(rows);
        }

        /* ----- clinical notes / non-medicine fields ----- */
        if (mode === "clinical" || mode === "all") {
        setForm((p) => {
            const n = {...p};
            REFILL_TEXT_FIELDS.forEach((f) => {
                if (v[f] !== null && v[f] !== undefined && String(v[f]).trim()) n[f] = String(v[f]);
            });
            const sym = String(v.symptom ?? "").trim();
            if (sym) {
                n.symptomChoice = SYMPTOMS.includes(sym) ? sym : "OTHER";
                n.otherSymptom = SYMPTOMS.includes(sym) ? "" : sym;
            }
            if (isDental) {
                if (v.otherdrugs) n.otherdrugs = String(v.otherdrugs);
                if (v.history) n.history = String(v.history);
                if (v.ispregnant != null && String(v.ispregnant).trim()) n.pregnant = String(v.ispregnant);
            }
            return n;
        });

        /* ----- department fields ----- */
        const pastDept = parseDept(v.department_data);
        if (Object.keys(pastDept).length) setDeptData((p) => ({...p, ...pastDept}));

        /* ----- dental ----- */
        if (isDental) {
            const toFlags = (csv) => Object.fromEntries(splitCsv(csv).filter(Boolean).map((k) => [k, true]));
            if (v.teeth_notes) {
                const t = parseTeethNotes(v.teeth_notes);
                setSelectedTooth(t.selected);
                setToothData(t.data);
            }
            if (v.chief_complaints) setChief(toFlags(v.chief_complaints));
            if (v.med_history) setMedFlags(toFlags(v.med_history));
        }
        }

        if (mode === "drugs" && missing.length) {
            Swal.fire({
                icon: "warning", title: "Some medicines are not in stock",
                html: `Pick a substitute for:<br><b>${missing.join("<br>")}</b>`,
            });
        } else {
            toast(mode === "drugs" ? "Medicines filled from previous visit" : "Clinical details filled from previous visit");
        }
    };

    const handleRefill = async (mode = "clinical") => {
        if (!a?.pat) return;
        setRefilling(true);

        try {
            const res = await axios.get(PAST_VISITS_URL(a.pat));
            const data = res.data;

            let list = Array.isArray(data) ? data
                : Array.isArray(data?.results) ? data.results
                : Array.isArray(data?.data) ? data.data
                : [];

            const today = new Date();
            const todayString = [
                today.getFullYear(),
                String(today.getMonth() + 1).padStart(2, "0"),
                String(today.getDate()).padStart(2, "0"),
            ].join("-");

            const toYMD = (value) => {
                const m = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
                return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
            };

            const getVisitDate = (visit) =>
                toYMD(visit.visit_date) || toYMD(visit.appt_date) || toYMD(visit.add_date) || toYMD(visit.date) ||
                toYMD(visit.appt?.appt_date) || toYMD(visit.emr?.add_date) || toYMD(visit.emr?.visit_date);

            const getPatientId = (visit) => String(
                visit.pat?.pat_id ?? visit.pat?.id ?? visit.patient?.pat_id ?? visit.patient?.id ??
                visit.pat_id ?? visit.patient_id ?? ""
            );

            const selectedPatientId = String(a.pat?.pat_id ?? a.pat?.id ?? a.pat ?? "");

            /* keep only this patient's earlier visits */
            list = list.filter((visit) => {
                const date = getVisitDate(visit);
                const pid = getPatientId(visit);
                return date && date < todayString && (!pid || pid === selectedPatientId);
            });

            /* newest first */
            list.sort((x, y) => getVisitDate(y).localeCompare(getVisitDate(x)) || Number(y.id || 0) - Number(x.id || 0));

            if (!list.length) {
                await Swal.fire("No previous visits", "No earlier visits were returned for this patient.", "info");
                return;
            }

            const options = {};
            list.forEach((visit, index) => {
                const rec = findCase(visit) || {};
                const diagnosis = String(rec.diagnosis || rec.complaints || visit.diagnosis || visit.complaints || "Clinical details not loaded").slice(0, 60);
                const date = getVisitDate(visit) || "Previous visit";
                const consultant = visit.cons?.consultant || visit.appt?.cons?.consultant || visit.consultant?.consultant || visit.consultant_name || "";
                options[String(index)] = `${date} - ${diagnosis}` + (consultant ? ` - ${consultant}` : "");
            });

            const result = await Swal.fire({
                title: mode === "drugs" ? "Auto Refill Drugs" : "Auto Refill Clinical Details",
                text: mode === "drugs"
                    ? "Select a previous consultation to load only its medicines."
                    : "Select a previous consultation to load clinical notes and non-medicine details.",
                input: "select",
                inputOptions: options,
                inputValue: "0",
                showCancelButton: true,
                confirmButtonText: "Fill from this visit",
            });

            if (result.isConfirmed && result.value !== "") {
                const selectedVisit = list[Number(result.value)];
                if (selectedVisit) await applyVisit(selectedVisit, mode);
            }
        } catch (error) {
            console.error("Auto Refill error:", error);
            await Swal.fire(
                "Could not load previous visits",
                error.response?.data?.detail || "Check the past-visits API URL, response structure, and patient ID.",
                "error"
            );
        } finally {
            setRefilling(false);
        }
    };

    /* =================================================================
       SAVE CASE SHEET
       ================================================================= */

    const submitHandler = async (e) => {
        e?.preventDefault();
        if (saving || !a) return;
        if (!consultationAppointmentId) {
            Swal.fire("Appointment reference missing", "The appointment API did not return its numeric appointment ID. Nothing was saved, to avoid linking this case sheet to the wrong OP-bill row.", "error");
            return;
        }

        const incomplete = drugRows.some((r) => !r.id && (r.pastName || r.remarks || r.freq || r.ddays || r.dose || r.dnotes));
        if (incomplete) {
            Swal.fire("Medicine not selected", "One or more rows have usage details but no medicine selected. Select a medicine or delete the row.", "warning");
            return;
        }

        const drugs = drugRows.filter((r) => r.id);
        const conId = conData.length > 0 ? conData[0].id : null;
        const fd = new FormData();
        fd.append("appt", String(consultationAppointmentId));
        fd.append("pat", a.pat.id);
        SAVE_KEYS.forEach((k) => fd.append(k, form[k] ?? ""));
        fd.append("symptom", form.symptomChoice === "OTHER" ? String(form.otherSymptom || "").trim() : String(form.symptomChoice || ""));
        fd.append("review_date", form.review_date || "");

        fd.append("drug_status", 1);
        if (drugs.length || conId) {
            fd.append("drug_id", joinTrim(drugs.map((r) => r.id)));
            fd.append("dose", joinTrim(drugs.map((r) => clean(r.dose))));
            fd.append("freq", joinTrim(drugs.map((r) => clean(r.freq))));
            fd.append("remarks", joinTrim(drugs.map((r) => clean(r.remarks))));
            fd.append("ddays", joinTrim(drugs.map((r) => clean(r.ddays))));
            fd.append("dnotes", joinTrim(drugs.map((r) => clean(r.dnotes))));
        }
        if (isDental) {
            fd.append("teeth_notes", selectedTooth.map((id) => {
                const t = toothData[id] || {};
                const hard = HARD_FIELDS.map((f) => `${f}:${clean(t.hard?.[f])}`).join(",");
                const soft = SOFT_FIELDS.map((f) => `${f}:${clean(t.soft?.[f])}`).join(",");
                return `${id}:${hard},${soft}`;
            }).join(";"));
            fd.append("chief_complaints", CHIEF_LIST.map(([k]) => k).filter((k) => chief[k]).join(","));
            fd.append("med_history", MEDHIST_LIST.map(([k]) => k).filter((k) => medFlags[k]).join(","));
            fd.append("otherdrugs", form.otherdrugs || "");
            fd.append("history", form.history || "");
            fd.append("ispregnant", form.pregnant || "");
        } else {
            const fields = DEPARTMENT_FIELDS[deptKey] || [];
            if (fields.length) {
                const out = {};
                fields.forEach((f) => {
                    out[f.name] = deptData[f.name] ?? "";
                });
                fd.append("department_data", JSON.stringify(out));
            }
        }

        fd.append("ip_status", ipStatus);
        fd.append("ip_add", ipAddress);
        fd.append("location", userLocation);
        if (!conId) {
            fd.append("status", "1");
            fd.append("addedby", userName);
        }

        setSaving(true);
        try {
            const res = conId
                ? await axios.put(`${baseUrl}edit-case/${conId}/`, fd)
                : await axios.post(`${baseUrl}consultform/`, fd);
            if (res.status === 200 || res.status === 201) {
                toast(conId ? "Details Updated." : "Details has been added.");
                hydratedRef.current = null; // re-read exactly what the server stored
                await fetchConData(consultationAppointmentId);
            }
        } catch (err) {
            console.log(err);
            Swal.fire("Error!", "Operation failed", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleComplete = (e) => {
        e.preventDefault();
        if (!a) return;
        const fd = new FormData();
        fd.append("appt_id", a.appt_id);
        fd.append("appt_date", a.appt_date);
        fd.append("type", a.type);
        fd.append("addedby", a.addedby);
        fd.append("status", "3");
        fd.append("ip_add", ipAddress);
        fd.append("location", userLocation);
        axios.put(`${baseUrl}update-appt/${appt_id}/`, fd)
            .then((r) => {
                if (r.status === 200 || r.status === 201) {
                    toast("Completed.");
                    setTimeout(() => window.open(`${window.location.origin}/op-case/${appt_id}`, "_blank"), 2000);
                }
            })
            .catch(() => Swal.fire("Error!", "Could not complete the consultation", "error"));
    };

    /* =================================================================
       LAB / RADIOLOGY / MIN ORDERS
       ================================================================= */

    const submitOrder = async (kind) => {
        const cfg = ORDER_CFG[kind], st = orderState[kind];
        const chosen = st.rows.filter((r) => String(r.id || "").trim() !== "");
        if (!chosen.length) {
            Swal.fire(cfg.emptyTitle, cfg.emptyText, "warning");
            return;
        }
        setSubmittingOrder(kind);
        try {
            const num = await axios.post(`${baseUrl}${cfg.numberUrl}`);
            const fd = new FormData();
            fd.append("appt_id", num.data.value);
            fd.append("pat", a.pat.id);
            fd.append("cons", a.cons.id);
            if (cfg.linkField) fd.append(cfg.linkField, consultationAppointmentId);
            fd.append(cfg.ids, joinTrim(chosen.map((r) => r.id)));
            fd.append(cfg.names, joinTrim(chosen.map((r) => r.name)));
            fd.append(cfg.amounts, joinTrim(chosen.map((r) => r.amount)));
            fd.append(cfg.tot, st.total);
            fd.append(cfg.disc, "0");
            fd.append(cfg.status, "1");
            fd.append("addedby", userName);
            fd.append("ip_add", ipAddress);
            fd.append("location", userLocation);
            const res = await axios.post(`${baseUrl}${cfg.createUrl}`, fd);
            if (res.status === 200 || res.status === 201) {
                toast(cfg.done);
                st.reset();
                if (kind === "lab") fetchLab();
                if (kind === "inv") fetchInv();
            }
        } catch (err) {
            console.log(err);
            Swal.fire("Error!", "Failed to add entry. Please try again later.", "error");
        } finally {
            setSubmittingOrder("");
        }
    };

    /* =================================================================
       MEDICAL FILES / 3D MAP
       ================================================================= */

    const formSubmitMed = async (e) => {
        e.preventDefault();
        if (!medFile.file) {
            Swal.fire("Select a file", "Please choose a file to upload.", "warning");
            return;
        }
        if (!consultationAppointmentId) {
            Swal.fire("Appointment reference missing", "Cannot upload this file because the numeric appointment ID was not returned.", "error");
            return;
        }
        const fd = new FormData();
        fd.append("appt", consultationAppointmentId);
        fd.append("file_type", medFile.ftype);
        fd.append("files", medFile.file, medFile.file.name);
        fd.append("comments", medFile.comments);
        fd.append("file_date", formatLocalDate(medicalDate));
        fd.append("status", "1");
        fd.append("addedby", userName);
        fd.append("ip_add", ipAddress);
        fd.append("location", userLocation);
        try {
            const res = await axios.post(`${baseUrl}op-file-create/`, fd);
            if (res.status === 200 || res.status === 201) {
                toast("File has been added.");
                setMedFile({ftype: "ctscan", comments: "", file: null});
                const input = document.getElementById("f_files");
                if (input) input.value = "";
                fetchFiles();
            }
        } catch (err) {
            console.log(err);
            Swal.fire("Error!", "Failed to upload file. Please try again later.", "error");
        }
    };

    const handleDeleteFile = (id) => {
        Swal.fire({title: "Confirm", text: "Are you Sure you want to delete this", icon: "info", confirmButtonText: "Continue", showCancelButton: true})
            .then((r) => {
                if (!r.isConfirmed) return;
                axios.delete(`${baseUrl}delete-files/${id}`)
                    .then(() => {
                        toast("Data has been Deleted");
                        fetchFiles();
                    })
                    .catch(() => Swal.fire("error", "Data has not been deleted"));
            });
    };

    const handlePointClick = (p) => setPoints((prev) => [...prev, {id: `${Date.now()}-${Math.random()}`, position: [p.x, p.y, p.z], label: ""}]);
    const handleLabelChange = (id, label) => setPoints((prev) => prev.map((p) => (p.id === id ? {...p, label} : p)));

    const handleSubmitMap = async () => {
        const conId = conData[0]?.id;
        if (!conId) return;
        const fd = new FormData();
        fd.append("coordinates", points.map((p) => p.position.map((n) => n.toFixed(2)).join(":")).join(","));
        fd.append("textmap", points.map((p) => p.label.trim()).join(","));
        fd.append("ip_add", ipAddress);
        fd.append("location", userLocation);
        try {
            const res = await axios.put(`${baseUrl}edit-case/${conId}/`, fd, {headers: {"Content-Type": "multipart/form-data"}});
            if (res.status === 200 || res.status === 201) toast("Details Updated.");
        } catch (err) {
            console.error(err);
            Swal.fire("Error!", "Failed to update", "error");
        }
    };

    /* =================================================================
       RENDER
       ================================================================= */

    const pat = a?.pat;
    const badge = (map) => (v) => {
        const s = String(v[map.key]);
        const m = map.items[s];
        return m ? <span className={`badge ${m[1]}`}>{m[0]}</span> : <span>unknown status</span>;
    };
    const labBadge = badge({key: "lab_status", items: {1: ["pending in cashier", "bg-warning"], 2: ["Sent to Lab", "bg-success"], 3: ["View", "bg-success"]}});
    const invBadge = badge({key: "inv_status", items: {1: ["pending in cashier", "bg-warning"], 2: ["Sent to Radiology", "bg-success"], 3: ["View", "bg-success"]}});
    const tabCount = {lab: labview.length, inv: invview.length, med: medview.length};

    return (
        <section className="container cd-page">

            {/* ---------- header ---------- */}
            <div className="cd-hero">
                <div className="cd-hero-title">
                    <span className="cd-hero-icon"><i className="fa-solid fa-stethoscope"/></span>
                    <div>
                        <small>Consultation</small>
                        <h4>Dr. {a?.cons?.consultant}<span> · {a?.cons?.department}</span></h4>
                    </div>
                </div>
                <div className="cd-hero-actions">
                    <form onSubmit={handleComplete} className="m-0">
                        <button type="submit" className="btn btn-warning"><i className="fa-solid fa-circle-check me-2"/>Complete Consultation</button>
                    </form>
                    <Link to="/cons-view/" className="btn btn-success"><i className="fa-solid fa-arrow-right me-2"/>Next Patient</Link>
                </div>
            </div>

            {/* ---------- patient ---------- */}
            {a && (
                <div className="cd-patient">
                    {[["Patient ID", pat.pat_id], ["Name", pat.patient], ["Gender", pat.gender], ["D.O.B/Age", pat.age], ["Mobile", pat.mobile], ["Regd. On", pat.add_date], ["Referred By", `${pat.refered ?? ""} ${pat.refconsultant ?? ""}`]].map(([l, v]) => (
                        <div key={l} className="cd-patient-item"><label>{l}</label><span>{v || "-"}</span></div>
                    ))}
                </div>
            )}

            {/* ---------- tabs ---------- */}
            <nav className="cd-tabs">
                {TABS.slice(0, 1).map((t) => (
                    <button key={t.id} type="button" className={`cd-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
                        <i className={`fa-solid ${t.icon}`}/>{t.label}
                    </button>
                ))}
                <Link className="cd-tab" to={`/emr-details/${pat?.pat_id ?? ""}`}><i className="fa-solid fa-clock-rotate-left"/>Past Visit</Link>
                {TABS.slice(1).map((t) => (
                    <button key={t.id} type="button" className={`cd-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
                        <i className={`fa-solid ${t.icon}`}/>{t.label}
                        {tabCount[t.id] > 0 && <em>{tabCount[t.id]}</em>}
                    </button>
                ))}
            </nav>

            <div className="cd-panel">

                {/* ================= LAB ================= */}
                {tab === "lab" && (
                    <OrderTab title="Lab Request" nameHeader="Lab Test Name" items={labitems} labelKey="subcat" state={lab}
                              onSubmit={() => submitOrder("lab")} submitting={submittingOrder === "lab"}
                              viewTitle="Lab Request View" viewRows={labview} viewNameKey="lab_test" statusBadge={labBadge}/>
                )}

                {/* ================= RADIOLOGY ================= */}
                {tab === "inv" && (
                    <OrderTab title="Radiology Request" nameHeader="Inv Test Name" items={invitems} labelKey="components" state={inv}
                              onSubmit={() => submitOrder("inv")} submitting={submittingOrder === "inv"}
                              viewTitle="Radiology Request View" viewRows={invview} viewNameKey="inv_test" statusBadge={invBadge}/>
                )}

                {/* ================= MIN TREAT ================= */}
                {tab === "min" && (
                    <OrderTab title="Minor Treatment" nameHeader="Min. Test Name" items={minitems} labelKey="comp" state={min}
                              onSubmit={() => submitOrder("min")} submitting={submittingOrder === "min"}/>
                )}

                {/* ================= 3D MAP ================= */}
                {tab === "map" && (
                    <>
                        <SectionTitle icon="fa-cube">Case Sheet Mapping</SectionTitle>
                        <div className="cd-canvas">
                            <Canvas className="canvas">
                                <OrbitControls enableZoom minDistance={1} maxDistance={20}/>
                                <ambientLight intensity={0.5}/>
                                <directionalLight position={[-2, 5, 2]} intensity={1}/>
                                <Suspense fallback={null}>
                                    <Anatomy onPointClick={handlePointClick}/>
                                    {points.map((p) => <Marker key={p.id} point={p} onLabelChange={handleLabelChange}/>)}
                                </Suspense>
                            </Canvas>
                        </div>
                        <button type="button" className="btn btn-primary mt-4" onClick={handleSubmitMap} disabled={!conData.length}>
                            <i className="fa-solid fa-floppy-disk me-2"/>Submit
                        </button>
                        {!conData.length && <small className="text-muted ms-3">Save the case sheet first to enable mapping.</small>}
                    </>
                )}

                {/* ================= MEDICAL SUMMARY ================= */}
                {tab === "med" && (
                    <>
                        <SectionTitle icon="fa-cloud-arrow-up">Medical File Upload</SectionTitle>
                        <form onSubmit={formSubmitMed} className="cd-upload">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label">Type of File</label>
                                    <select className="form-select" id="ftype" value={medFile.ftype} onChange={(e) => setMedFile((p) => ({...p, ftype: e.target.value}))}>
                                        <option value="ctscan">CT-Scan</option>
                                        <option value="xray">X-Ray</option>
                                        <option value="casesheet">Case Sheet</option>
                                        <option value="other">Other Docs</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">File Created Date</label>
                                    <DatePicker selected={medicalDate} onChange={(d) => d && setMedicalDate(d)} dateFormat="yyyy-MM-dd" className="form-control"/>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">File Upload</label>
                                    <input type="file" className="form-control" id="f_files" onChange={(e) => setMedFile((p) => ({...p, file: e.target.files[0] || null}))}/>
                                </div>
                                <div className="col-12">
                                    <label className="form-label">Comments</label>
                                    <textarea className="form-control no-comma" rows={3} value={medFile.comments} onChange={(e) => setMedFile((p) => ({...p, comments: e.target.value}))}/>
                                </div>
                            </div>
                            <button type="submit" className="btn btn-primary mt-3"><i className="fa-solid fa-upload me-2"/>Submit</button>
                        </form>

                        <SectionTitle icon="fa-folder-open">Medical Document View</SectionTitle>
                        <div className="table-responsive">
                            <table className="erp-table-modern cd-table">
                                <thead><tr><th>Appt ID</th><th>File Type</th><th>File Date</th><th>Comments</th><th>View</th><th>Addedby</th><th>Action</th></tr></thead>
                                <tbody>
                                {medview.map((m) => (
                                    <tr key={m.id}>
                                        <td>{m.appt?.appt_id}</td><td>{m.file_type}</td><td>{m.file_date}</td><td>{m.comments}</td>
                                        <td><iframe src={m.files} title={String(m.files)} allowFullScreen/></td>
                                        <td>{m.addedby}</td>
                                        <td><button onClick={() => handleDeleteFile(m.id)} className="btn btn-outline-danger btn-sm"><i className="fa-solid fa-trash-can"/></button></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {/* ================= CASE SHEET ================= */}
                {tab === "case" && (
                    <form onSubmit={submitHandler} noValidate>



                        {/* vitals */}
                        <SectionTitle icon="fa-heart-pulse">Vitals & Symptoms</SectionTitle>
                        <div className="row">
                            {VITALS.map(([name, label, type, icon, ro]) => (
                                <div key={name} className="col-lg-4 col-md-6 mb-3">
                                    <label className="form-label">{label}</label>
                                    <div className="input-group">
                                        <span className="input-group-text"><i className={`fa-solid ${icon}`}/></span>
                                        <input type={type} className="form-control" placeholder={label} name={name} value={form[name]} onChange={inputHandler} readOnly={!!ro}/>
                                    </div>
                                </div>
                            ))}
                            <div className="col-lg-4 col-md-6 mb-3">
                                <label className="form-label">Symptoms</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="fa-solid fa-stethoscope"/></span>
                                    <select className="form-select" name="symptomChoice" value={form.symptomChoice} onChange={inputHandler}>
                                        <option value="">--Select--</option>
                                        {SYMPTOMS.map((s) => <option key={s} value={s}>{s}</option>)}
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>
                                {form.symptomChoice === "OTHER" && (
                                    <input type="text" className="form-control mt-2" name="otherSymptom" placeholder="Enter symptom" value={form.otherSymptom} onChange={inputHandler}/>
                                )}
                            </div>
                        </div>
{/* quick fill */}
                        <div className="cd-quick">
                            <div className="cd-quick-text">
                                <b><i className="fa-solid fa-wand-magic-sparkles me-2"/>Quick Fill</b>
                                <small>Refill clinical details</small>
                            </div>
                            <div className="cd-quick-actions">
                                <button type="button" className="btn btn-outline-primary" onClick={() => handleRefill("clinical")} disabled={refilling || !a}>
                                    <i className="fa-solid fa-file-medical me-2"/>{refilling ? "Loading..." : "Auto Refill (Clinical)"}
                                </button>

                            </div>
                        </div>

                        {/* department examination */}
                        {!isDental && (DEPARTMENT_FIELDS[deptKey] || []).length > 0 && (
                            <>
                                <SectionTitle icon="fa-user-doctor">Department Examination</SectionTitle>
                                <div className="row">
                                    {DEPARTMENT_FIELDS[deptKey].map((f) => <NoteField key={f.name} label={f.label} rows={5} {...noteProps(`dept:${f.name}`)}/>)}
                                </div>
                            </>
                        )}

                        {/* general notes */}
                        {!isDental && (
                            <>
                                <SectionTitle icon="fa-file-pen">Clinical Notes</SectionTitle>
                                <div className="row">
                                    {GENERAL_NOTES.map(([name, label]) => <NoteField key={name} label={label} rows={6} {...noteProps(name)}/>)}
                                </div>
                            </>
                        )}

                        {/* ---------- DENTAL ---------- */}
                        {isDental && (
                            <>
                                <SectionTitle icon="fa-tooth">Chief Complaint</SectionTitle>
                                <div className="cd-checks">
                                    {CHIEF_LIST.map(([k, l]) => (
                                        <label key={k} className="cd-check">
                                            <input type="checkbox" checked={!!chief[k]} onChange={(e) => setChief((p) => ({...p, [k]: e.target.checked}))}/>
                                            <span>{l}</span>
                                        </label>
                                    ))}
                                </div>
                                <div className="row mt-3">
                                    <NoteField label="Complaints" {...noteProps("complaints")}/>
                                </div>

                                <SectionTitle icon="fa-notes-medical">Medical History</SectionTitle>
                                <div className="cd-checks">
                                    {MEDHIST_LIST.map(([k, l]) => (
                                        <label key={k} className="cd-check">
                                            <input type="checkbox" checked={!!medFlags[k]} onChange={(e) => setMedFlags((p) => ({...p, [k]: e.target.checked}))}/>
                                            <span>{l}</span>
                                        </label>
                                    ))}
                                </div>
                                <div className="row mt-3">
                                    <div className="col-12 mb-3">
                                        <label className="form-label">Are you taking any drugs now? Please specify</label>
                                        <input type="text" className="form-control" name="otherdrugs" value={form.otherdrugs} onChange={inputHandler}/>
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label">Have you had any serious problems / abnormal bleeding associated with previous Dental / Surgery</label>
                                        <input type="text" className="form-control" name="history" value={form.history} onChange={inputHandler}/>
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label">Are you pregnant? (if female)</label>
                                        <input type="text" className="form-control" name="pregnant" placeholder="If yes, how many months" value={form.pregnant} onChange={inputHandler}/>
                                    </div>
                                </div>
                                <div className="row">
                                    <NoteField label="Allergies" {...noteProps("allergy")}/>
                                    <NoteField label="General Examination" {...noteProps("examination")}/>
                                    <NoteField label="Past History" {...noteProps("illness")}/>
                                    <NoteField label="Diagnosis" {...noteProps("diagnosis")}/>
                                </div>

                                <SectionTitle icon="fa-teeth">Oral Examination <small>(click a tooth to add notes)</small></SectionTitle>
                                <div className="cd-chart">
                                    <svg viewBox="0 0 820 420" width="100%" height="420">
                                        <ellipse cx="410" cy="200" rx="160" ry="190" fill="#fff7f1" stroke="#f0c8b6" strokeWidth="2"/>
                                        {LOWER_TEETH.map((t) => (
                                            <g key={t.id} onClick={() => toggleTooth(t.id)} style={{cursor: "pointer"}}>
                                                <circle cx={t.x} cy={420 - t.y + 250} r={16} fill={selectedTooth.includes(t.id) ? "#fef08a" : "#ffffff"} stroke="#333" strokeWidth="1.2"/>
                                                <text x={t.x} y={420 - t.y + 255} textAnchor="middle" fontSize="10" fill="#111">{t.label}</text>
                                            </g>
                                        ))}
                                        {UPPER_TEETH.map((t) => (
                                            <g key={t.id} onClick={() => toggleTooth(t.id)} style={{cursor: "pointer"}}>
                                                <circle cx={t.x} cy={420 - (t.y + 250)} r={16} fill={selectedTooth.includes(t.id) ? "#fef08a" : "#ffffff"} stroke="#333" strokeWidth="1.2"/>
                                                <text x={t.x} y={420 - (t.y + 245)} textAnchor="middle" fontSize="10" fill="#111">{t.label}</text>
                                            </g>
                                        ))}
                                        <line x1="410" y1="40" x2="410" y2="380" stroke="#ddd" strokeDasharray="4 4"/>
                                    </svg>
                                </div>

                                <h6 className="mt-4">Selected Teeth Notes</h6>
                                {selectedTooth.length ? selectedTooth.map((id) => {
                                    const t = toothData[id] || {};
                                    return (
                                        <div key={id} className="cd-tooth">
                                            <div className="cd-tooth-title">Tooth #{id}</div>
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">Hard Tissue</label>
                                                    {HARD_FIELDS.map((f) => (
                                                        <textarea key={f} rows={2} className="form-control mb-2" placeholder={f} value={t.hard?.[f] || ""} onChange={(e) => updateTooth(id, "hard", f, e.target.value)}/>
                                                    ))}
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">Soft Tissue</label>
                                                    {SOFT_FIELDS.map((f) => (
                                                        <textarea key={f} rows={2} className="form-control mb-2" placeholder={f} value={t.soft?.[f] || ""} onChange={(e) => updateTooth(id, "soft", f, e.target.value)}/>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }) : <div className="text-muted fst-italic">No teeth selected. Click any tooth above to add notes.</div>}

                                <h6 className="mt-4">Intraoral Examination</h6>
                                <div className="cd-checks">
                                    {["Good", "Fair", "Poor"].map((o) => (
                                        <label key={o} className="cd-check"><input type="radio" name="oralHygiene"/><span>{o}</span></label>
                                    ))}
                                </div>

                                <div className="row mt-4">
                                    <NoteField label="Treatment Advised" col="col-md-6" {...noteProps("tadvise")}/>
                                    <NoteField label="Treatment Suggested" col="col-md-6" {...noteProps("tsugg")}/>
                                </div>
                            </>
                        )}
{/* quick fill */}
                        <div className="cd-quick">
                            <div className="cd-quick-text">
                                <b><i className="fa-solid fa-wand-magic-sparkles me-2"/>Quick Fill</b>
                                <small>Refill medicines, or insert favourite medicines.</small>
                            </div>
                            <div className="cd-quick-actions">
                         
                                <button type="button" className="btn btn-outline-success" onClick={() => handleRefill("drugs")} disabled={refilling || !a}>
                                    <i className="fa-solid fa-pills me-2"/>{refilling ? "Loading..." : "Auto Refill Drugs"}
                                </button>
                                <button type="button" className={`btn ${showFavs ? "btn-warning" : "btn-outline-warning"}`} onClick={() => setShowFavs((s) => !s)}>
                                    <i className="fa-solid fa-star me-2"/>Favourite Medicines <em>{favs.length}</em>
                                </button>
                            </div>
                        </div>
                        {showFavs && (
                            <div className="cd-favs">
                                {favs.length === 0 && <small className="text-muted">No favourites yet. Click the ☆ on any medicine row to save it here.</small>}
                                {favs.map((f) => (
                                    <span key={f.name} className="cd-fav-chip">
                                        <button type="button" onClick={() => addFavToRows(f)} title="Add to prescription">
                                            <i className="fa-solid fa-plus me-1"/>{f.name}{f.remarks ? ` · ${f.remarks}` : ""}
                                        </button>
                                        <i className="fa-solid fa-xmark" title="Remove" onClick={() => persistFavs(favs.filter((x) => x.name !== f.name))}/>
                                    </span>
                                ))}
                            </div>
                        )}
                        {/* ---------- MEDICINE ---------- */}
                        <SectionTitle icon="fa-pills" right={<small className="text-muted">Shift = add row · Alt = delete row · ☆ = favourite</small>}>Medicine</SectionTitle>
                        <div className="table-responsive">
                            <table className="erp-table-modern cd-table">
                                <thead>
                                <tr>
                                    <th style={{width: 60}}>ID</th><th style={{minWidth: 280}}>Drug name</th><th>Dose</th>
                                    <th>Usage</th><th style={{width: 90}}>Days</th><th style={{width: 90}}>QTY</th><th>Notes</th><th style={{width: 110}}>Action</th>
                                </tr>
                                </thead>
                                <tbody>
                                {drugRows.map((row, i) => {
                                    const opt = optionFor(row);
                                    return (
                                        <tr key={row.key}>
                                            <td><input type="text" className="form-control" value={row.id} readOnly tabIndex={-1}/></td>
                                            <td>
                                                <AutoComplete
                                                    options={opt?.synthetic ? [...drugitems, opt] : drugitems}
                                                    value={opt}
                                                    isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                                                    getOptionLabel={(o) => o?.product?.product || ""}
                                                    filterOptions={drugFilter}
                                                    onChange={(e, val) => selectDrug(i, val)}
                                                    onKeyDown={(e) => onDrugKeyDown(e, i)}
                                                    renderOption={(props, option) => {
                                                        const {key, ...rest} = props;
                                                        return (
                                                            <li key={option.id ?? key} {...rest}>
                                                                <div style={{width: "100%"}}>
                                                                    <div style={{fontWeight: 600}}>{option.product?.product || ""}</div>
                                                                    <div style={{fontSize: 13, color: "#555"}}>Generic: {option.product?.generic || "-"}</div>
                                                                    <div style={{fontSize: 12, color: "#777"}}>
                                                                        Batch: {option.product?.batch || "-"} | Expy: {option.product?.expiry || "-"} | Qty: {option.qty ?? 0}
                                                                    </div>
                                                                </div>
                                                            </li>
                                                        );
                                                    }}
                                                    renderInput={(params) => (
                                                        <div ref={params.InputProps.ref}>
                                                            <input {...params.inputProps} data-drug-key={row.key} className="form-control cd-ac-input" type="text" placeholder="Type 3+ letters"/>
                                                        </div>
                                                    )}
                                                />
                                                {row.pastName && !row.id && <small className="cd-warn"><i className="fa-solid fa-triangle-exclamation me-1"/>Not in stock: {row.pastName} - pick a substitute</small>}
                                            </td>
                                            <td>
                                                <input type="text" list={`rem-${row.key}`} className="form-control" placeholder="Dose" value={row.remarks} onChange={(e) => updateDrugField(i, "remarks", e.target.value)}/>
                                                <datalist id={`rem-${row.key}`}>{REMARK_OPTIONS.map((o) => <option key={o} value={o}/>)}</datalist>
                                            </td>
                                            <td>
                                                <input type="text" list={`frq-${row.key}`} className="form-control" placeholder="Usage" value={row.freq} onChange={(e) => updateDrugField(i, "freq", e.target.value)}/>
                                                <datalist id={`frq-${row.key}`}>{FREQ_OPTIONS.map((o) => <option key={o} value={o}/>)}</datalist>
                                            </td>
                                            <td><input type="number" min="1" className="form-control" value={row.ddays} onChange={(e) => updateDrugField(i, "ddays", e.target.value)}/></td>
                                            <td><input type="number" className="form-control" value={row.dose} onChange={(e) => updateDrugField(i, "dose", e.target.value)}/></td>
                                            <td><input type="text" className="form-control" value={row.dnotes} onChange={(e) => updateDrugField(i, "dnotes", e.target.value)}/></td>
                                            <td className="cd-row-actions">
                                                <button type="button" className={`btn btn-sm cd-star ${isFav(row) ? "on" : ""}`} disabled={!row.id} onClick={() => toggleFav(row)} title="Favourite">
                                                    <i className={`${isFav(row) ? "fa-solid" : "fa-regular"} fa-star`}/>
                                                </button>
                                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeDrugRow(i)}><i className="fa-solid fa-trash-can"/></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                        <button className="btn btn-outline-success mt-3" type="button" onClick={addDrugRow}><i className="fa-solid fa-plus me-2"/>Add More</button>

                        {/* ---------- medicine request view ---------- */}
                        <SectionTitle icon="fa-prescription-bottle-medical">Medicine Request View</SectionTitle>
                        <div className="table-responsive">
                            <table className="erp-table-modern cd-table">
                                <thead>
                                <tr><th>Drug ID</th><th>Drug Name</th><th>Dose</th><th>Usage</th><th>Days</th><th>Qty</th><th>Notes</th><th>Addedby</th><th>Action</th></tr>
                                </thead>
                                <tbody>
                                {conData.map((row) => {
                                    const ids = splitCsv(row.drug_id), rem = splitCsv(row.remarks), fq = splitCsv(row.freq);
                                    const dd = splitCsv(row.ddays), ds = splitCsv(row.dose), nt = splitCsv(row.dnotes);
                                    return ids.map((id, i) => !id ? null : (
                                        <tr key={`${row.id}-${id}-${i}`}>
                                            <td>{id}</td><td>{nameForId(id) || "-"}</td><td>{rem[i] || "-"}</td><td>{fq[i] || "-"}</td>
                                            <td>{dd[i] || "-"}</td><td>{ds[i] || "-"}</td><td>{nt[i] || "-"}</td><td>{row.addedby || "-"}</td>
                                            <td>
                                                {String(row.drug_status) === "1" ? <span className="badge bg-warning">Sent To Pharmacy</span>
                                                    : String(row.drug_status) === "2" ? <span className="badge bg-success">Completed</span>
                                                        : <span className="badge bg-secondary">{row.drug_status || "Unknown"}</span>}
                                            </td>
                                        </tr>
                                    ));
                                })}
                                </tbody>
                            </table>
                        </div>

                        {/* ---------- follow-up ---------- */}
                        <SectionTitle icon="fa-calendar-check">Follow-up</SectionTitle>
                        <div className="row">
                            <div className="col-lg-2 col-md-6 mb-3">
                                <label className="form-label">Review Days</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="fa-solid fa-clock"/></span>
                                    <input type="text" className="form-control" placeholder="e.g. 7" value={form.review_days} onChange={handleReviewDaysChange}/>
                                </div>
                            </div>
                            <div className="col-lg-2 col-md-6 mb-3">
                                <label className="form-label">Review</label>
                                <DatePicker id="Review" selected={selectedDate} onChange={handleDateChange} dateFormat="yyyy-MM-dd"
                                            placeholderText="Select date" className="form-control" popperPlacement="top-start" popperClassName="review-datepicker-popper"/>
                            </div>
                            <div className="col-lg-2 col-md-6 mb-3">
                                <label className="form-label">SOS</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="fa-solid fa-hourglass-half"/></span>
                                    <select className="form-select" name="duration_tt" value={form.duration_tt} onChange={inputHandler}>
                                        <option value="">Select</option><option value="No">No</option><option value="Yes">Yes</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <label className="form-label">Patient Type</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="fa-solid fa-user-tag"/></span>
                                    <select className="form-select" name="pat_type" value={form.pat_type} onChange={inputHandler}>
                                        <option value="">Select Type</option>
                                        {["Kidney", "Normal", "Testis", "Penis Meatus", "Abdomen"].map((o) => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <label className="form-label">Admission Req:</label>
                                <div className="form-check form-switch mt-2">
                                    <input className="form-check-input" type="checkbox" id="ipStatus" checked={ipStatus === 1} onChange={(e) => setIpStatus(e.target.checked ? 1 : 0)}/>
                                    <label className="form-check-label" htmlFor="ipStatus">Yes</label>
                                </div>
                            </div>
                        </div>

                        <div className="cd-savebar">
                            <button type="submit" className="btn btn-success" disabled={saving}>
                                <i className="fa-solid fa-floppy-disk me-2"/>{saving ? "Saving..." : conData.length ? "Update Case Sheet" : "Save Case Sheet"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </section>
    );
}

export default ConsDetail;